/**
 * @file AgencyMarketPage.jsx
 * @description Shared visual-first layout for the agency market pages (Florida,
 *   Miami, Charlotte). Each page passes its own copy and visual config; this file
 *   owns the structure: hero -> coverage visual + one SEO paragraph -> post/match/hire
 *   flow -> "we help fill your case" distribution -> what providers see vs what stays
 *   private -> differentiator grid -> founder card -> related links. No closing CTA band (hero is the only CTA). No local counts or
 *   claims beyond what the page passes in.
 * @module components/AgencyMarketPage
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import { BriefcaseIcon, ChevronRightIcon, SearchIcon, UsersIcon, CheckIcon } from "./Icons";
import PublicFooter from "./PublicFooter";
import StickyMobileCta from "./StickyMobileCta";
import AgencyPricingNote from "./AgencyPricingNote";
import { useAgencySignup } from "./AgencyFinalCta";
import ContactModal from "./ContactModal";
import { ProofChips, FlowSteps, CasePreview, ReachRings, RegionList, DifferentialGrid, FounderCard, OutsideFloridaNote } from "./AgencyVisuals";
import CasePromotion from "./CasePromotion";
import { seoHref } from "../lib/seo";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

const FLOW_ICONS = [BriefcaseIcon, SearchIcon, UsersIcon, CheckIcon];

/**
 * @param {object} p
 * @param {string} p.source analytics source id
 * @param {string} p.pagePath e.g. "/aba-staffing-florida/"
 * @param {string} p.market analytics market id
 * @param {string} p.crumb breadcrumb label for this page
 * @param {string} p.badge small badge above the H1
 * @param {string} p.title H1
 * @param {string} p.subtitle one-line subtitle
 * @param {{Icon:Function,label:string}[]} p.chips proof chips (facts only)
 * @param {string} p.coverageTitle
 * @param {string} p.body one short SEO paragraph next to the visual
 * @param {{type:"rings",center:string,towns:string[]}|{type:"regions",regions:string[]}} p.visual
 * @param {string} p.area area label used in the case preview
 * @param {{to:string,label:string}[]} p.links related internal links
 * @param {boolean} [p.showOutsideFloridaNote] show the "Hiring outside Florida?" note under the distribution flow.
 *   Only the Charlotte page passes it.
 * @param {Function} p.onNavigate
 */
export default function AgencyMarketPage(p) {
  const { t } = useTranslation();
  const [contactOpen, setContactOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const { label, go } = useAgencySignup({ source: p.source, pagePath: p.pagePath, market: p.market, onNavigate: p.onNavigate });

  const SeoLink = ({ to, style, children }) => (
    <a href={to === "home" ? "/" : seoHref(to)} onClick={(e) => { e.preventDefault(); p.onNavigate(to); }} style={{ textDecoration: "none", ...style }}>{children}</a>
  );
  const sectionPad = isMobile ? "44px 18px" : "64px 48px";
  const maxW = 980;
  const h2 = { fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 24px", letterSpacing: "-0.02em", textAlign: "center" };

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 44px" : "32px 48px 60px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          <nav aria-label="Breadcrumb" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 20, fontSize: 12.5 }}>
            <SeoLink to="home" style={{ color: INDIGO_LT }}>{t("agencyMarket.crumbHome")}</SeoLink>
            <ChevronRightIcon size={12} color="rgba(255,255,255,0.4)" />
            <SeoLink to="aba-agencies" style={{ color: INDIGO_LT }}>{t("agencyMarket.crumbAgencies")}</SeoLink>
            <ChevronRightIcon size={12} color="rgba(255,255,255,0.4)" />
            <span style={{ color: "#CBD5E1", fontWeight: 600 }}>{p.crumb}</span>
          </nav>
          <span style={{ display: "inline-block", fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", marginBottom: 14 }}>{p.badge}</span>
          <h1 style={{ fontSize: isMobile ? 29 : 44, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.12, letterSpacing: "-0.025em", maxWidth: 760 }}>{p.title}</h1>
          <p style={{ fontSize: isMobile ? 16 : 19, color: "#cbd5e1", lineHeight: 1.5, maxWidth: 640, margin: "0 0 24px" }}>{p.subtitle}</p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={() => go("hero")} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: isMobile ? "13px 24px" : "15px 30px", background: T.gradientIndigo, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 24px rgba(79,70,229,0.4)" }}>
              <BriefcaseIcon size={16} color="#fff" /> {label}
            </button>
            <SeoLink to="aba-agencies" style={{ color: "#fff", fontWeight: 700, fontSize: 14.5, padding: "12px 8px" }}>{t("agencyMarket.seeHow")}</SeoLink>
          </div>
          <AgencyPricingNote style={{ color: "#A5B4FC", fontSize: 14, marginTop: 12, maxWidth: 620 }} />
          <div style={{ marginTop: 20 }}><ProofChips dark items={p.chips} /></div>
        </div>
      </header>

      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.1fr 1fr", gap: isMobile ? 24 : 48, alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
            {p.visual.type === "rings"
              ? <ReachRings isMobile={isMobile} center={p.visual.center} towns={p.visual.towns} />
              : <RegionList regions={p.visual.regions} />}
          </div>
          <div>
            <h2 style={{ ...h2, textAlign: "left", margin: "0 0 12px" }}>{p.coverageTitle}</h2>
            <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.7, margin: 0 }}>{p.body}</p>
          </div>
        </div>
      </section>

      <section style={{ padding: sectionPad, background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={h2}>{t("agencyMarket.flowTitle")}</h2>
          <FlowSteps isMobile={isMobile} steps={FLOW_ICONS.map((Icon, i) => ({ Icon, title: t(`agencyLanding.flow${i + 1}Title`), sub: t(`agencyLanding.flow${i + 1}Sub`) }))} />
        </div>
      </section>

      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={h2}>{t("agencyVisuals.promoTitle")}</h2>
          <CasePromotion isMobile={isMobile} />
          {p.showOutsideFloridaNote && <OutsideFloridaNote isMobile={isMobile} />}
        </div>
      </section>

      <section style={{ padding: sectionPad, background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
        <h2 style={h2}>{t("agencyMarket.previewTitle")}</h2>
        <CasePreview isMobile={isMobile} area={p.area} />
      </section>

      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={h2}>{t("agencyVisuals.diffTitle")}</h2>
          <DifferentialGrid />
        </div>
      </section>

      <section style={{ padding: sectionPad, background: T.surfaceAlt, borderTop: `1px solid ${BORDER}` }}>
        <FounderCard isMobile={isMobile} onMessage={() => setContactOpen(true)} />
        {p.links?.length > 0 && (
          <p style={{ textAlign: "center", fontSize: 14, color: TEXT_MID, margin: "32px 0 0", lineHeight: 1.9 }}>
            {t("agencyMarket.related")}{" "}
            {p.links.map((l, i) => (
              <span key={l.to}>{i > 0 && " · "}<SeoLink to={l.to} style={{ color: INDIGO, fontWeight: 700 }}>{l.label}</SeoLink></span>
            ))}
          </p>
        )}
      </section>

      {contactOpen && <ContactModal onClose={() => setContactOpen(false)} />}
      <PublicFooter />
      <StickyMobileCta label={label} onClick={() => go("sticky")} ctaId="agency" />
    </div>
  );
}
